import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Home } from "lucide-react";

const EMPTY = { full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", label: "Home", is_default: false };

export default function Addresses() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    refresh();
  }, [user, authLoading, navigate]);

  const refresh = () => api.get("/addresses").then((r) => setList(r.data));

  const openNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const openEdit = (a) => { setEditing({ ...a }); setOpen(true); };

  const save = async () => {
    if (!/^\d{10}$/.test(editing.phone)) return toast.error("Valid 10-digit phone required");
    if (!/^\d{6}$/.test(editing.pincode)) return toast.error("Valid 6-digit pincode required");
    try {
      if (editing.id) await api.put(`/addresses/${editing.id}`, editing);
      else await api.post("/addresses", editing);
      toast.success("Address saved");
      setOpen(false); refresh();
    } catch (e) { toast.error("Could not save"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await api.delete(`/addresses/${id}`);
    toast.success("Deleted");
    refresh();
  };

  if (authLoading) return <div className="text-center py-20 font-display text-2xl text-deepbrown/60">Loading…</div>;
  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="addresses-page">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-rani font-bold">Account</p>
          <h1 className="font-display text-4xl sm:text-5xl text-deepbrown font-bold mt-2">Address Book</h1>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-rani text-white font-bold" data-testid="add-address-btn">
          <Plus className="h-4 w-4" /> Add Address
        </button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 mt-8 text-center border border-deepbrown/10">
          <Home className="h-12 w-12 text-rani mx-auto mb-3" />
          <p className="font-display text-xl text-deepbrown">No saved addresses</p>
          <p className="text-deepbrown/60 mt-1">Add one to make checkout faster.</p>
        </div>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {list.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl p-5 border border-deepbrown/10 relative" data-testid={`address-${a.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-display text-lg text-deepbrown">{a.label || "Address"} {a.is_default && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold bg-emerald text-white px-2 py-0.5 rounded-full">Default</span>}</p>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(a)} className="h-8 w-8 rounded-full bg-marigold/20 grid place-items-center hover:bg-marigold/40" data-testid={`edit-address-${a.id}`}>
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button onClick={() => del(a.id)} className="h-8 w-8 rounded-full bg-rani/10 grid place-items-center hover:bg-rani hover:text-white" data-testid={`delete-address-${a.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <p className="text-sm mt-3 leading-relaxed text-deepbrown/80">
                <b>{a.full_name}</b><br />
                {a.line1}, {a.line2 && `${a.line2}, `}<br />
                {a.city}, {a.state} – {a.pincode}<br />
                <span className="text-deepbrown/60">Phone: {a.phone}</span>
              </p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing?.id ? "Edit address" : "New address"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Label</Label>
                  <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="Home, Office..." />
                </div>
                <div>
                  <Label>Full name</Label>
                  <Input value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} data-testid="addr-name" />
                </div>
              </div>
              <div>
                <Label>Phone (10 digits)</Label>
                <Input value={editing.phone} maxLength={10} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} data-testid="addr-phone" />
              </div>
              <div>
                <Label>Address line 1</Label>
                <Input value={editing.line1} onChange={(e) => setEditing({ ...editing, line1: e.target.value })} data-testid="addr-line1" />
              </div>
              <div>
                <Label>Address line 2 (optional)</Label>
                <Input value={editing.line2} onChange={(e) => setEditing({ ...editing, line2: e.target.value })} data-testid="addr-line2" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>City</Label>
                  <Input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} data-testid="addr-city" />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })} data-testid="addr-state" />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input value={editing.pincode} maxLength={6} onChange={(e) => setEditing({ ...editing, pincode: e.target.value })} data-testid="addr-pincode" />
                </div>
              </div>
              <div className="flex items-center justify-between bg-marigold/10 p-3 rounded-xl">
                <Label className="m-0">Set as default</Label>
                <Switch checked={editing.is_default} onCheckedChange={(v) => setEditing({ ...editing, is_default: v })} />
              </div>
              <button onClick={save} className="w-full py-3 rounded-full bg-rani text-white font-bold" data-testid="save-address-btn">Save Address</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
